/**
 * Comprehensive cookie management utility for NextJS
 * Works in both server and client environments
 */

import { NextRequest, NextResponse } from "next/server";
import { getDomainInfo, isProductionDomain } from "./domainDetection";

export interface CookieOptions {
  name: string;
  value: string;
  domain?: string;
  path?: string;
  maxAge?: number;
  expires?: Date;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
}

export interface ChunkedCookieOptions extends Omit<CookieOptions, "value"> {
  value: string;
  maxChunkSize?: number;
}

export interface CookieManagerOptions {
  defaultDomain?: string;
  defaultPath?: string;
  defaultMaxAge?: number;
  defaultSecure?: boolean;
  defaultSameSite?: "Strict" | "Lax" | "None";
  enableDebug?: boolean;
}

/**
 * Cookie manager for NextJS applications
 * Handles both server-side and client-side cookie operations
 */
export class CookieManager {
  private options: CookieManagerOptions;
  private isServer: boolean;

  constructor(options: CookieManagerOptions = {}) {
    this.options = {
      defaultDomain: options.defaultDomain,
      defaultPath: options.defaultPath || "/",
      defaultMaxAge: options.defaultMaxAge || 604800, // 7 days
      defaultSecure: options.defaultSecure ?? isProductionDomain(),
      defaultSameSite: options.defaultSameSite || "Lax",
      enableDebug: options.enableDebug ?? false,
      ...options,
    };
    this.isServer = typeof window === "undefined";
  }

  /**
   * Set a cookie (client-side)
   */
  setCookie(options: CookieOptions): void {
    if (this.isServer) {
      throw new Error("setCookie can only be called on the client side");
    }

    const cookieString = this.buildCookieString(options);
    document.cookie = cookieString;

    if (this.options.enableDebug) {
      console.log(
        "[CookieManager] Set cookie:",
        options.name,
        "Length:",
        options.value.length
      );
    }
  }

  /**
   * Set a cookie (server-side with NextResponse)
   */
  setCookieServer(
    response: NextResponse,
    options: CookieOptions
  ): NextResponse {
    const cookieString = this.buildCookieString(options);
    response.cookies.set(options.name, options.value, {
      domain: options.domain || this.options.defaultDomain,
      path: options.path || this.options.defaultPath,
      maxAge: options.maxAge || this.options.defaultMaxAge,
      expires: options.expires,
      secure: options.secure ?? this.options.defaultSecure,
      httpOnly: options.httpOnly ?? false,
      sameSite: options.sameSite || this.options.defaultSameSite,
    });

    if (this.options.enableDebug) {
      console.log("[CookieManager] Set server cookie:", options.name);
    }

    return response;
  }

  /**
   * Get a cookie value (client-side)
   */
  getCookie(name: string): string | null {
    if (this.isServer) {
      throw new Error("getCookie can only be called on the client side");
    }

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(";").shift();
      return cookieValue ? decodeURIComponent(cookieValue) : null;
    }
    return null;
  }

  /**
   * Get a cookie value (server-side from NextRequest)
   */
  getCookieServer(request: NextRequest, name: string): string | null {
    return request.cookies.get(name)?.value || null;
  }

  /**
   * Remove a cookie (client-side)
   */
  removeCookie(name: string, domain?: string): void {
    if (this.isServer) {
      throw new Error("removeCookie can only be called on the client side");
    }

    const domainPart = domain ? `; Domain=${domain}` : "";
    document.cookie = `${name}=; Path=/; Max-Age=0${domainPart}`;

    if (this.options.enableDebug) {
      console.log("[CookieManager] Removed cookie:", name);
    }
  }

  /**
   * Remove a cookie (server-side with NextResponse)
   */
  removeCookieServer(
    response: NextResponse,
    name: string,
    domain?: string
  ): NextResponse {
    response.cookies.delete({
      name,
      domain: domain || this.options.defaultDomain,
      path: this.options.defaultPath,
    });

    if (this.options.enableDebug) {
      console.log("[CookieManager] Removed server cookie:", name);
    }

    return response;
  }

  /**
   * Set a large cookie using chunking
   */
  setChunkedCookie(options: ChunkedCookieOptions): void {
    if (this.isServer) {
      throw new Error("setChunkedCookie can only be called on the client side");
    }

    const maxChunkSize = options.maxChunkSize || 4000;
    const chunks = Math.ceil(options.value.length / maxChunkSize);

    // Set chunk count
    this.setCookie({
      ...options,
      name: `${options.name}_chunks`,
      value: chunks.toString(),
    });

    // Set individual chunks
    for (let i = 0; i < chunks; i++) {
      const start = i * maxChunkSize;
      const end = Math.min(start + maxChunkSize, options.value.length);
      const chunk = options.value.substring(start, end);

      this.setCookie({
        ...options,
        name: `${options.name}_${i}`,
        value: chunk,
      });
    }

    if (this.options.enableDebug) {
      console.log(
        "[CookieManager] Set chunked cookie:",
        options.name,
        "Chunks:",
        chunks
      );
    }
  }

  /**
   * Set a large cookie using chunking (server-side)
   */
  setChunkedCookieServer(
    response: NextResponse,
    options: ChunkedCookieOptions
  ): NextResponse {
    const maxChunkSize = options.maxChunkSize || 4000;
    const chunks = Math.ceil(options.value.length / maxChunkSize);

    // Set chunk count
    response = this.setCookieServer(response, {
      ...options,
      name: `${options.name}_chunks`,
      value: chunks.toString(),
    });

    // Set individual chunks
    for (let i = 0; i < chunks; i++) {
      const start = i * maxChunkSize;
      const end = Math.min(start + maxChunkSize, options.value.length);
      const chunk = options.value.substring(start, end);

      response = this.setCookieServer(response, {
        ...options,
        name: `${options.name}_${i}`,
        value: chunk,
      });
    }

    if (this.options.enableDebug) {
      console.log(
        "[CookieManager] Set chunked server cookie:",
        options.name,
        "Chunks:",
        chunks
      );
    }

    return response;
  }

  /**
   * Get a chunked cookie value
   */
  getChunkedCookie(name: string): string | null {
    if (this.isServer) {
      throw new Error("getChunkedCookie can only be called on the client side");
    }

    const chunks = this.getCookie(`${name}_chunks`);
    if (!chunks) {
      return null;
    }

    const chunkCount = parseInt(chunks, 10);
    if (isNaN(chunkCount)) {
      return null;
    }

    let result = "";
    for (let i = 0; i < chunkCount; i++) {
      const chunk = this.getCookie(`${name}_${i}`);
      if (!chunk) {
        return null; // Missing chunk
      }
      result += chunk;
    }

    return result;
  }

  /**
   * Get a chunked cookie value (server-side)
   */
  getChunkedCookieServer(request: NextRequest, name: string): string | null {
    const chunks = this.getCookieServer(request, `${name}_chunks`);
    if (!chunks) {
      return null;
    }

    const chunkCount = parseInt(chunks, 10);
    if (isNaN(chunkCount)) {
      return null;
    }

    let result = "";
    for (let i = 0; i < chunkCount; i++) {
      const chunk = this.getCookieServer(request, `${name}_${i}`);
      if (!chunk) {
        return null; // Missing chunk
      }
      result += chunk;
    }

    return result;
  }

  /**
   * Remove a chunked cookie
   */
  removeChunkedCookie(name: string, domain?: string): void {
    if (this.isServer) {
      throw new Error(
        "removeChunkedCookie can only be called on the client side"
      );
    }

    const chunks = this.getCookie(`${name}_chunks`);
    if (chunks) {
      const chunkCount = parseInt(chunks, 10);
      for (let i = 0; i < chunkCount; i++) {
        this.removeCookie(`${name}_${i}`, domain);
      }
      this.removeCookie(`${name}_chunks`, domain);
    }

    if (this.options.enableDebug) {
      console.log("[CookieManager] Removed chunked cookie:", name);
    }
  }

  /**
   * Remove a chunked cookie (server-side)
   */
  removeChunkedCookieServer(
    response: NextResponse,
    name: string,
    domain?: string
  ): NextResponse {
    const chunks = this.getCookieServer(response as any, `${name}_chunks`);
    if (chunks) {
      const chunkCount = parseInt(chunks, 10);
      for (let i = 0; i < chunkCount; i++) {
        response = this.removeCookieServer(response, `${name}_${i}`, domain);
      }
      response = this.removeCookieServer(response, `${name}_chunks`, domain);
    }

    if (this.options.enableDebug) {
      console.log("[CookieManager] Removed chunked server cookie:", name);
    }

    return response;
  }

  /**
   * Build cookie string from options
   */
  private buildCookieString(options: CookieOptions): string {
    const domain = options.domain || this.options.defaultDomain;
    const path = options.path || this.options.defaultPath;
    const maxAge = options.maxAge || this.options.defaultMaxAge;
    const secure = options.secure ?? this.options.defaultSecure;
    const sameSite = options.sameSite || this.options.defaultSameSite;
    const httpOnly = options.httpOnly ?? false;

    let cookieString = `${options.name}=${encodeURIComponent(options.value)}`;

    if (path) cookieString += `; Path=${path}`;
    if (domain) cookieString += `; Domain=${domain}`;
    if (maxAge) cookieString += `; Max-Age=${maxAge}`;
    if (options.expires)
      cookieString += `; Expires=${options.expires.toUTCString()}`;
    if (secure) cookieString += "; Secure";
    if (httpOnly) cookieString += "; HttpOnly";
    if (sameSite) cookieString += `; SameSite=${sameSite}`;

    return cookieString;
  }

  /**
   * Get all cookies as an object
   */
  getAllCookies(): Record<string, string> {
    if (this.isServer) {
      throw new Error("getAllCookies can only be called on the client side");
    }

    const cookies: Record<string, string> = {};
    document.cookie.split(";").forEach((cookie) => {
      const [name, value] = cookie.trim().split("=");
      if (name && value) {
        cookies[name] = decodeURIComponent(value);
      }
    });
    return cookies;
  }

  /**
   * Get all cookies as an object (server-side)
   */
  getAllCookiesServer(request: NextRequest): Record<string, string> {
    const cookies: Record<string, string> = {};
    request.cookies.forEach((cookie, name) => {
      cookies[name] = cookie.value;
    });
    return cookies;
  }

  /**
   * Clear all cookies
   */
  clearAllCookies(): void {
    if (this.isServer) {
      throw new Error("clearAllCookies can only be called on the client side");
    }

    const cookies = this.getAllCookies();
    Object.keys(cookies).forEach((name) => {
      this.removeCookie(name);
    });

    if (this.options.enableDebug) {
      console.log("[CookieManager] Cleared all cookies");
    }
  }

  /**
   * Check if a cookie exists
   */
  hasCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }

  /**
   * Check if a cookie exists (server-side)
   */
  hasCookieServer(request: NextRequest, name: string): boolean {
    return this.getCookieServer(request, name) !== null;
  }

  /**
   * Get cookie size in bytes
   */
  getCookieSize(name: string): number {
    const value = this.getCookie(name);
    return value ? new Blob([value]).size : 0;
  }

  /**
   * Validate cookie size
   */
  validateCookieSize(value: string, maxSize: number = 4096): boolean {
    return new Blob([value]).size <= maxSize;
  }
}

/**
 * Create a cookie manager instance
 */
export function createCookieManager(
  options?: CookieManagerOptions
): CookieManager {
  return new CookieManager(options);
}

/**
 * Default cookie manager instance
 */
export const cookieManager = createCookieManager({
  enableDebug: process.env.NODE_ENV === "development",
});

/**
 * Session-specific cookie utilities
 */
export class SessionCookieManager extends CookieManager {
  private sessionKey: string;

  constructor(
    sessionKey: string = "SUPABASE_SESSION",
    options?: CookieManagerOptions
  ) {
    super(options);
    this.sessionKey = sessionKey;
  }

  /**
   * Set session cookie
   */
  setSession(sessionData: any): void {
    const sessionString = JSON.stringify(sessionData);

    if (this.validateCookieSize(sessionString)) {
      this.setCookie({
        name: this.sessionKey,
        value: sessionString,
        domain: this.options.defaultDomain,
        secure: this.options.defaultSecure,
        sameSite: this.options.defaultSameSite,
      });
    } else {
      this.setChunkedCookie({
        name: this.sessionKey,
        value: sessionString,
        domain: this.options.defaultDomain,
        secure: this.options.defaultSecure,
        sameSite: this.options.defaultSameSite,
      });
    }
  }

  /**
   * Set session cookie (server-side)
   */
  setSessionServer(response: NextResponse, sessionData: any): NextResponse {
    const sessionString = JSON.stringify(sessionData);

    if (this.validateCookieSize(sessionString)) {
      return this.setCookieServer(response, {
        name: this.sessionKey,
        value: sessionString,
        domain: this.options.defaultDomain,
        secure: this.options.defaultSecure,
        sameSite: this.options.defaultSameSite,
      });
    } else {
      return this.setChunkedCookieServer(response, {
        name: this.sessionKey,
        value: sessionString,
        domain: this.options.defaultDomain,
        secure: this.options.defaultSecure,
        sameSite: this.options.defaultSameSite,
      });
    }
  }

  /**
   * Get session data
   */
  getSession(): any | null {
    const sessionString =
      this.getChunkedCookie(this.sessionKey) || this.getCookie(this.sessionKey);
    if (!sessionString) return null;

    try {
      return JSON.parse(sessionString);
    } catch (error) {
      if (this.options.enableDebug) {
        console.error("[SessionCookieManager] Error parsing session:", error);
      }
      return null;
    }
  }

  /**
   * Get session data (server-side)
   */
  getSessionServer(request: NextRequest): any | null {
    const sessionString =
      this.getChunkedCookieServer(request, this.sessionKey) ||
      this.getCookieServer(request, this.sessionKey);
    if (!sessionString) return null;

    try {
      return JSON.parse(sessionString);
    } catch (error) {
      if (this.options.enableDebug) {
        console.error("[SessionCookieManager] Error parsing session:", error);
      }
      return null;
    }
  }

  /**
   * Clear session
   */
  clearSession(): void {
    this.removeChunkedCookie(this.sessionKey, this.options.defaultDomain);
    this.removeCookie(this.sessionKey, this.options.defaultDomain);
  }

  /**
   * Clear session (server-side)
   */
  clearSessionServer(response: NextResponse): NextResponse {
    response = this.removeChunkedCookieServer(
      response,
      this.sessionKey,
      this.options.defaultDomain
    );
    response = this.removeCookieServer(
      response,
      this.sessionKey,
      this.options.defaultDomain
    );
    return response;
  }
}

/**
 * Create a session cookie manager instance
 */
export function createSessionCookieManager(
  sessionKey?: string,
  options?: CookieManagerOptions
): SessionCookieManager {
  return new SessionCookieManager(sessionKey, options);
}
